import React from "react";
import { Link } from "react-router-dom";

import fleetcorePageHeroAccountabilityDashboardDesktop from "../../images/fleetcore-page-hero-accountability-dashboard-desktop.png";
import fleetcorePageHeroAccountabilityDashboardMobile from "../../images/fleetcore-page-hero-accountability-dashboard-mobile.png";
import fleetcorePageHeroLuminousOrbitsBackground from "../../images/fleetcore-page-hero-luminous-orbits-background.svg";

import "./FleetCorePageHero.css";

function FleetCorePageHeroArrowIcon({
  className = "fleetcore-page-hero__action-arrow",
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M2.5 9H15M10.5 4.5L15 9L10.5 13.5"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FleetCorePageHeroLoginIcon() {
  return (
    <svg
      className="fleetcore-page-hero__system-login-icon"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="4.25"
        y="8.25"
        width="11.5"
        height="8.5"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />

      <path
        d="M6.75 8.25V6.5C6.75 4.70507 8.20507 3.25 10 3.25C11.7949 3.25 13.25 4.70507 13.25 6.5V8.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      <path
        d="M10 11.25V13.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function FleetCorePageHero({
  demonstrationHref = "#fleetcore-demonstration",
  loginHref = "/fleetcore/login",
  demonstrationTarget,
  onBrochureClick,
}) {
  const backgroundStyle = {
    "--fleetcore-page-hero-orbits-background": `url("${fleetcorePageHeroLuminousOrbitsBackground}")`,
  };

  return (
    <section
      id="fleetcore-page-top"
      className="fleetcore-page-hero"
      aria-labelledby="fleetcore-page-hero-title"
      aria-describedby="fleetcore-page-hero-description"
      style={backgroundStyle}
    >
      <div className="fleetcore-page-hero__inner">
        <div className="fleetcore-page-hero__copy">
          <p className="fleetcore-page-hero__eyebrow">
            FleetCore
          </p>

          <h1
            id="fleetcore-page-hero-title"
            className="fleetcore-page-hero__title"
          >
            <span className="fleetcore-page-hero__title-layout fleetcore-page-hero__title-layout--desktop">
              Know every litre.
              <br />
              Account for every
              <br />
              kilometre.
            </span>

            <span className="fleetcore-page-hero__title-layout fleetcore-page-hero__title-layout--landscape">
              Know every
              <br />
              litre. Account
              <br />
              for every
              <br />
              kilometre.
            </span>

            <span className="fleetcore-page-hero__title-layout fleetcore-page-hero__title-layout--portrait">
              Know every litre.
              <br />
              Account for every
              <br />
              kilometre.
            </span>

            <span className="fleetcore-page-hero__title-layout fleetcore-page-hero__title-layout--mobile">
              Know every litre.
              <br />
              Account for
              <br />
              every kilometre.
            </span>
          </h1>

          <p
            id="fleetcore-page-hero-description"
            className="fleetcore-page-hero__body"
          >
            <span className="fleetcore-page-hero__body-layout fleetcore-page-hero__body-layout--desktop">
              FleetCore connects driver identity, vehicle condition, mileage
              <br />
              and refuelling evidence into one accountable operating system.
            </span>

            <span className="fleetcore-page-hero__body-layout fleetcore-page-hero__body-layout--landscape">
              Connect every driver, vehicle, reading and litre
              <br />
              inside one accountable fleet operating system.
            </span>

            <span className="fleetcore-page-hero__body-layout fleetcore-page-hero__body-layout--portrait">
              FleetCore connects driver identity, vehicle condition, mileage and
              <br />
              refuelling evidence into one accountable fleet operating system.
            </span>

            <span className="fleetcore-page-hero__body-layout fleetcore-page-hero__body-layout--mobile">
              FleetCore connects driver identity, vehicle
              <br />
              condition, mileage and refuelling evidence
              <br />
              into one accountable fleet operating system.
            </span>
          </p>

          <div className="fleetcore-page-hero__action-area">
            <div className="fleetcore-page-hero__actions">
              <a
                className="fleetcore-page-hero__action fleetcore-page-hero__action--primary"
                href={demonstrationHref}
                target={demonstrationTarget}
                rel={
                  demonstrationTarget === "_blank"
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                <span className="fleetcore-page-hero__action-label">
                  Book a demonstration
                </span>

                <FleetCorePageHeroArrowIcon />
              </a>

              <button
                type="button"
                className="fleetcore-page-hero__action fleetcore-page-hero__action--secondary"
                onClick={onBrochureClick}
              >
                <span className="fleetcore-page-hero__action-label">
                  Download brochure
                </span>

                <FleetCorePageHeroArrowIcon />
              </button>
            </div>

            <Link
              className="fleetcore-page-hero__system-login"
              to={loginHref}
              aria-label="Open the official FleetCore system login"
              title="Official FleetCore system login"
            >
              <FleetCorePageHeroLoginIcon />

              <span className="fleetcore-page-hero__system-login-label">
                FleetCore login
              </span>

              <FleetCorePageHeroArrowIcon className="fleetcore-page-hero__system-login-arrow" />
            </Link>
          </div>
        </div>

        <figure className="fleetcore-page-hero__visual">
          <picture className="fleetcore-page-hero__dashboard-picture">
            <source
              media="(max-width: 699px)"
              srcSet={fleetcorePageHeroAccountabilityDashboardMobile}
            />

            <img
              className="fleetcore-page-hero__dashboard"
              src={fleetcorePageHeroAccountabilityDashboardDesktop}
              alt="FleetCore accountability dashboard showing a commercial vehicle, verified driver, recorded mileage, refuelling evidence, 98 percent of fuel accounted for and no detected fuel anomaly."
              loading="eager"
              decoding="async"
              draggable="false"
            />
          </picture>

          <figcaption className="fleetcore-page-hero__proof">
            <span
              className="fleetcore-page-hero__proof-dot"
              aria-hidden="true"
            />

            <span className="fleetcore-page-hero__proof-text">
              AI-assisted · Evidence-led
            </span>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
